import { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import {
  listProjects,
  createProject,
  updateProject,
  deleteProject,
} from "../../../services/projects";
import type { ProjectItem } from "../../../services/projects";
import { departments as departmentsApi } from "../../../services/admin";
import type { DepartmentItem } from "../../../services/admin";
import { ensurePermission } from "../../../utils/permissions";
import RIcon from "../../../components/RIcon";

type Status = "进行中" | "已完成" | "暂停";

const statusStyle: Record<Status, { cls: string; dot: string }> = {
  "进行中": { cls: "bg-[#0EA5A0]/10 text-[#0EA5A0]", dot: "bg-[#0EA5A0]" },
  "已完成": { cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  "暂停": { cls: "bg-gray-100 text-gray-500", dot: "bg-gray-400" },
};

interface Project {
  id: number;
  name: string;
  code: string;
  leader: string;
  department: string;
  progress: number;
  status: Status;
  startDate: string;
  desc: string;
}

function mapProjectItem(item: ProjectItem): Project {
  return {
    id: item.id,
    name: item.name,
    code: item.code,
    leader: item.leader ?? "待指派",
    department: item.department ?? "待分配",
    progress: item.progress,
    status: (item.status as Status) || "进行中",
    startDate: item.start_date?.slice(0, 10) ?? "",
    desc: item.description ?? "",
  };
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [departmentList, setDepartmentList] = useState<DepartmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", code: "", startDate: "", leader: "", department: "", desc: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 2200); };

  const loadProjects = useCallback(async () => {
    try {
      const items = await listProjects();
      setProjects(items.map(mapProjectItem));
    } catch (err) {
      showToast(err instanceof Error ? err.message : "加载项目失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    departmentsApi.list().then(setDepartmentList).catch(() => {});
  }, [loadProjects]);

  const departmentNames = departmentList.map((d) => d.name);

  const resetForm = () => setForm({
    name: "", code: "",
    startDate: "", leader: "", department: "", desc: "",
  });

  const validate = (f: typeof form) => {
    const errs: Record<string, string> = {};
    if (!f.name.trim()) errs.name = "请输入项目名称";
    if (!f.code.trim()) errs.code = "请输入项目编号";
    if (!f.startDate) errs.startDate = "请选择开始日期";
    return errs;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensurePermission(user, "projects:write", showToast)) return;
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await createProject({
        name: form.name,
        code: form.code || undefined,
        leader: form.leader || undefined,
        department: form.department || undefined,
        start_date: form.startDate || undefined,
        description: form.desc || undefined,
      });
      setCreateOpen(false);
      resetForm();
      setErrors({});
      showToast("项目创建成功");
      await loadProjects();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensurePermission(user, "projects:write", showToast)) return;
    if (!editProject) return;
    setSubmitting(true);
    try {
      await updateProject(editProject.id, {
        name: editProject.name,
        code: editProject.code,
        leader: editProject.leader,
        department: editProject.department,
        progress: editProject.progress,
        status: editProject.status,
        start_date: editProject.startDate || undefined,
        description: editProject.desc || undefined,
      });
      setEditProject(null);
      showToast("项目信息已更新");
      await loadProjects();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "更新失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!ensurePermission(user, "projects:delete", showToast)) return;
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      showToast("项目已删除");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "删除失败");
    }
    setDeleteId(null);
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">项目中心</h2>
          <p className="text-xs text-[#718096] mt-0.5">文件资源分类，用于按项目维度归档与检索</p>
        </div>
        <button
          onClick={() => {
            if (!ensurePermission(user, "projects:write", showToast)) return;
            resetForm();
            setCreateOpen(true);
          }}
          className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] text-white rounded-lg text-sm font-medium hover:bg-[#0D9590] cursor-pointer whitespace-nowrap"
        >
          <RIcon name="add-line" />新建项目
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {([["进行中", "text-[#0EA5A0]", "bg-[#0EA5A0]/10"], ["已完成", "text-emerald-600", "bg-emerald-50"], ["暂停", "text-gray-500", "bg-gray-50"]] as const).map(([label, color, bg]) => (
          <div key={label} className={`${bg} rounded-xl p-4`}>
            <div className={`text-2xl font-bold ${color}`}>{projects.filter((p) => p.status === label).length}</div>
            <div className="text-xs text-[#718096] mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <RIcon name="loader-4-line" className="animate-spin text-3xl text-[#0EA5A0] block mb-3" />
            <p className="text-sm text-[#718096]">加载中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                  {["项目名称", "项目描述", "项目编号", "负责人", "所属部门", "进度", "状态", "开始日期", "操作"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((proj) => (
                  <tr key={proj.id} className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
                    <td className="px-4 py-3 max-w-48">
                      <Link to={`/workbench/projects/${proj.id}`} className="font-medium text-[#0EA5A0] hover:underline truncate block cursor-pointer">{proj.name}</Link>
                    </td>
                    <td className="px-4 py-3 max-w-40"><span className="text-xs text-[#4A5568] line-clamp-2">{proj.desc || "-"}</span></td>
                    <td className="px-4 py-3"><span className="text-[#0EA5A0] font-mono text-xs">{proj.code}</span></td>
                    <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{proj.leader}</td>
                    <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{proj.department}</td>
                    <td className="px-4 py-3 w-40">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0EA5A0] rounded-full transition-all" style={{ width: `${proj.progress}%` }} />
                        </div>
                        <span className="text-xs text-[#718096] whitespace-nowrap">{proj.progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${statusStyle[proj.status]?.cls || "bg-gray-100 text-gray-500"}`}>{proj.status}</span>
                    </td>
                    <td className="px-4 py-3 text-[#718096] whitespace-nowrap">{proj.startDate}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/workbench/projects/${proj.id}`)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] transition-colors cursor-pointer" title="查看详情">
                          <RIcon name="eye-line" className="text-sm" />
                        </button>
                        <button onClick={() => ensurePermission(user, "projects:write", showToast) && setEditProject({ ...proj })} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] transition-colors cursor-pointer" title="编辑">
                          <RIcon name="edit-line" className="text-sm" />
                        </button>
                        <button onClick={() => ensurePermission(user, "projects:delete", showToast) && setDeleteId(proj.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 transition-colors cursor-pointer" title="删除">
                          <RIcon name="delete-bin-line" className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">新建项目</h3>
              <button onClick={() => setCreateOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"><RIcon name="close-line" className="text-[#718096]" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目名称 <span className="text-red-500">*</span></label>
                  <input type="text" placeholder="请输入项目名称" className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目编号 <span className="text-red-500">*</span></label>
                  <input type="text" className={`w-full border rounded-lg px-3 h-10 text-sm outline-none font-mono ${errors.code ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                  {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">开始日期 <span className="text-red-500">*</span></label>
                  <input type="date" className={`w-full border rounded-lg px-3 h-10 text-sm outline-none cursor-pointer ${errors.startDate ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                  {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">所属部门</label>
                  <Select
                    showSearch
                    allowClear
                    placeholder="请选择"
                    style={{ width: "100%" }}
                    value={form.department || undefined}
                    onChange={(v) => setForm({ ...form, department: (v ?? "") as string })}
                    optionFilterProp="label"
                    options={departmentNames.map((d) => ({ value: d, label: d }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目负责人</label>
                <input type="text" placeholder="请输入负责人姓名" className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]" value={form.leader} onChange={(e) => setForm({ ...form, leader: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目描述</label>
                <textarea rows={2} placeholder="可选" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] resize-none" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} maxLength={500} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setCreateOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={submitting} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {submitting ? <><RIcon name="loader-4-line" className="animate-spin" />创建中...</> : "创建项目"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editProject && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑项目</h3>
              <button onClick={() => setEditProject(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"><RIcon name="close-line" className="text-[#718096]" /></button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目名称</label>
                  <input type="text" className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]" value={editProject.name} onChange={(e) => setEditProject({ ...editProject, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目编号</label>
                  <input type="text" className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0] font-mono" value={editProject.code} onChange={(e) => setEditProject({ ...editProject, code: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">负责人</label>
                  <input type="text" className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]" value={editProject.leader} onChange={(e) => setEditProject({ ...editProject, leader: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">所属部门</label>
                  <Select
                    showSearch
                    allowClear
                    placeholder="请选择"
                    style={{ width: "100%" }}
                    value={editProject.department || undefined}
                    onChange={(v) => setEditProject({ ...editProject, department: (v ?? "") as string })}
                    optionFilterProp="label"
                    options={departmentNames.map((d) => ({ value: d, label: d }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目状态</label>
                  <Select
                    style={{ width: "100%" }}
                    value={editProject.status}
                    onChange={(v) => setEditProject({ ...editProject, status: v as Status })}
                    options={[
                      { value: "进行中", label: "进行中" },
                      { value: "已完成", label: "已完成" },
                      { value: "暂停", label: "暂停" },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">完成进度（{editProject.progress}%）</label>
                  <input type="range" min={0} max={100} step={5} className="w-full mt-2 accent-[#0EA5A0] cursor-pointer" value={editProject.progress} onChange={(e) => setEditProject({ ...editProject, progress: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">项目描述</label>
                <textarea
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] min-h-[80px]"
                  value={editProject.desc || ""}
                  onChange={(e) => setEditProject({ ...editProject, desc: e.target.value })}
                  placeholder="项目的详细描述..."
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditProject(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={submitting} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {submitting ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存修改"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RIcon name="delete-bin-line" className="text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#1A2B3C] text-center mb-1">确认删除项目</h3>
            <p className="text-sm text-[#718096] text-center mb-6">删除后项目数据将无法恢复，请谨慎操作</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
